import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { MessageSquare, Send, Inbox, Reply, User, Users, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const FeedbackSection = () => {
  const { user } = useAuth();
  const [feedbacks, setFeedbacks] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [sentBroadcasts, setSentBroadcasts] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [recipientCounts, setRecipientCounts] = useState({});
  const [composeOpen, setComposeOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ to_user_id: '', feedback_type: 'cadangan', subject: '', message: '' });
  const [broadcastForm, setBroadcastForm] = useState({ recipient_type: 'individual', recipient_id: '', subject: '', message: '' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API}/api/feedback`, { withCredentials: true }),
        axios.get(`${API}/api/feedback/recipients`, { withCredentials: true }),
      ];

      // Add broadcast endpoints for non-admin or admin-specific data
      if (user?.role === 'admin') {
        requests.push(axios.get(`${API}/api/broadcast/sent`, { withCredentials: true }));
        requests.push(axios.get(`${API}/api/broadcast/recipient-counts`, { withCredentials: true }));
      }
      requests.push(axios.get(`${API}/api/broadcast`, { withCredentials: true }));

      const responses = await Promise.all(requests);
      const [fbRes, recRes, ...rest] = responses;
      setFeedbacks(fbRes.data);
      setRecipients(recRes.data);
      
      let broadcastIdx = 0;
      if (user?.role === 'admin') {
        setSentBroadcasts(rest[broadcastIdx].data);
        broadcastIdx++;
        setRecipientCounts(rest[broadcastIdx].data);
        broadcastIdx++;
      }
      setBroadcasts(rest[broadcastIdx].data);
    } catch (err) {
      console.error('Failed to load feedback/broadcast:', err);
      toast.error('Failed to load messages');
    } finally { 
      setLoading(false); 
    }
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const inbox = useMemo(() => feedbacks.filter((f) => f.to_user_id === user?.id), [feedbacks, user]);
  const sent = useMemo(() => feedbacks.filter((f) => f.user_id === user?.id), [feedbacks, user]);
  const broadcastInbox = useMemo(() => broadcasts.filter((b) => b.to_user_id === user?.id), [broadcasts, user]);
  const unreadCount = useMemo(() => inbox.filter((f) => !f.is_read).length + broadcastInbox.filter((b) => !b.is_read).length, [inbox, broadcastInbox]);

  const openCompose = () => {
    setReplyTo(null);
    setForm({ to_user_id: '', feedback_type: 'cadangan', subject: '', message: '' });
    setComposeOpen(true);
  };

  const openBroadcast = () => {
    setBroadcastForm({ recipient_type: 'individual', recipient_id: '', subject: '', message: '' });
    setBroadcastOpen(true);
  };

  const openReply = (fb) => {
    setReplyTo(fb);
    setForm({
      to_user_id: fb.user_id,
      feedback_type: 'cadangan',
      subject: fb.subject.startsWith('Re: ') ? fb.subject : `Re: ${fb.subject}`,
      message: '',
    });
    setComposeOpen(true);
  };

  const sendFeedback = async (e) => {
    e.preventDefault();
    if (!form.to_user_id || !form.feedback_type || !form.subject || !form.message) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      const payload = { ...form };
      if (replyTo) payload.parent_id = replyTo.parent_id || replyTo.id;
      await axios.post(`${API}/api/feedback`, payload, { withCredentials: true });
      toast.success(replyTo ? 'Reply sent' : 'Feedback sent');
      setComposeOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
  };

  const sendBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastForm.subject || !broadcastForm.message) {
      toast.error('Please fill subject and message');
      return;
    }
    if (broadcastForm.recipient_type === 'individual' && !broadcastForm.recipient_id) {
      toast.error('Please select a recipient');
      return;
    }
    try {
      const payload = {
        subject: broadcastForm.subject,
        message: broadcastForm.message,
        recipient_type: broadcastForm.recipient_type,
      };
      if (broadcastForm.recipient_type === 'individual') {
        payload.recipient_id = broadcastForm.recipient_id;
      }
      await axios.post(`${API}/api/broadcast`, payload, { withCredentials: true });
      toast.success('Message sent to ' + (broadcastForm.recipient_type === 'individual' ? 'recipient' : broadcastForm.recipient_type.replace('all_', 'all ').replace('_', ' ')));
      setBroadcastOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
  };

  const markRead = async (fbId, type = 'feedback') => {
    try {
      const endpoint = type === 'broadcast' ? `${API}/api/broadcast/${fbId}/read` : `${API}/api/feedback/${fbId}/read`;
      await axios.patch(endpoint, {}, { withCredentials: true });
      fetchAll();
    } catch (e) { /* silent */ }
  };

  const renderItem = (fb, kind) => {
    const isBroadcast = fb.recipient_type !== undefined;
    return (
      <div
        key={fb.id}
        className={`p-4 border rounded-lg ${kind === 'inbox' && !fb.is_read ? 'border-blue-300 bg-blue-50/40' : 'border-slate-200 bg-white'}`}
        data-testid={`feedback-${kind}-item-${fb.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-slate-900 truncate">{fb.subject}</h4>
              {kind === 'inbox' && !fb.is_read && (
                <Badge className="bg-blue-100 text-blue-700 border-blue-300 border text-[10px]">NEW</Badge>
              )}
              {isBroadcast && (
                <Badge className="bg-purple-100 text-purple-700 border-purple-300 border text-[10px]" title={fb.recipient_type}>
                  <Megaphone className="w-3 h-3 mr-1" /> {fb.recipient_type === 'individual' ? 'Message' : 'Broadcast'}
                </Badge>
              )}
              {!isBroadcast && fb.parent_id && <Badge className="bg-slate-100 text-slate-700 border-slate-300 border text-[10px]">Reply</Badge>}
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-2">
              <User className="w-3 h-3" />
              {kind === 'inbox' ? (
                <>From {fb.user_name} {!isBroadcast && `(${fb.user_role})`} • {new Date(fb.created_at).toLocaleString()}</>
              ) : (
                <>To {isBroadcast ? `${fb.recipient_type.replace('all_', 'all ').replace('_', ' ')} (${fb.recipient_count} recipients)` : `${fb.to_user_name} (${fb.to_user_role})`} • {new Date(fb.created_at).toLocaleString()}</>
              )}
            </p>
            <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{fb.message}</p>
          </div>
          <div className="flex flex-col gap-2">
            {kind === 'inbox' && (
              <>
                {!fb.is_read && (
                  <Button size="sm" variant="outline" onClick={() => markRead(fb.id, isBroadcast ? 'broadcast' : 'feedback')} data-testid={`mark-read-${fb.id}`}>
                    Mark read
                  </Button>
                )}
                {!isBroadcast && (
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => openReply(fb)} data-testid={`reply-${fb.id}`}>
                    <Reply className="w-3 h-3 mr-1" /> Reply
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  const allInbox = [...inbox, ...broadcastInbox].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const allSent = [...sent, ...(user?.role === 'admin' ? sentBroadcasts : [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <Card className="bg-white border-slate-200" data-testid="feedback-section">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Messages {unreadCount > 0 && <Badge className="ml-2 bg-blue-100 text-blue-700 border-blue-300 border">{unreadCount} unread</Badge>}
          </CardTitle>
          <CardDescription>
            {user?.role === 'admin' ? 'Send messages to individuals or broadcast to groups' : 'Messages with admins'}
          </CardDescription>
        </div>
        <div className="flex gap-2">
          {user?.role === 'admin' && (
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={openBroadcast} data-testid="compose-broadcast-button">
              <Megaphone className="w-4 h-4 mr-2" /> Broadcast
            </Button>
          )}
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCompose} data-testid="compose-feedback-button">
            <Send className="w-4 h-4 mr-2" /> Message
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="inbox">
          <TabsList data-testid="feedback-tabs">
            <TabsTrigger value="inbox" data-testid="tab-inbox">
              <Inbox className="w-4 h-4 mr-2" /> Inbox ({allInbox.length})
            </TabsTrigger>
            <TabsTrigger value="sent" data-testid="tab-sent">
              <Send className="w-4 h-4 mr-2" /> Sent ({allSent.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="inbox" className="mt-4 space-y-3" data-testid="feedback-inbox-list">
            {allInbox.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                <p>Your inbox is empty</p>
              </div>
            ) : allInbox.map((fb) => renderItem(fb, 'inbox'))}
          </TabsContent>
          <TabsContent value="sent" className="mt-4 space-y-3" data-testid="feedback-sent-list">
            {allSent.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <Send className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                <p>No sent messages yet</p>
              </div>
            ) : allSent.map((fb) => renderItem(fb, 'sent'))}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Regular Message Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-lg" data-testid="feedback-compose-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              {replyTo ? 'Reply' : 'New Message'}
            </DialogTitle>
            <DialogDescription>
              {replyTo ? `Replying to ${replyTo.user_name}` : (user?.role === 'admin' ? 'Send a message to a teacher or parent' : 'Send a message to an admin')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendFeedback} className="space-y-4" data-testid="feedback-form">
            <div>
              <Label>Recipient</Label>
              <Select value={form.to_user_id} onValueChange={(v) => setForm({ ...form, to_user_id: v })} disabled={!!replyTo}>
                <SelectTrigger data-testid="feedback-recipient-select">
                  <SelectValue placeholder="Select recipient" />
                </SelectTrigger>
                <SelectContent>
                  {(replyTo ? [{ id: replyTo.user_id, name: replyTo.user_name, role: replyTo.user_role }] : recipients).map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name} {r.department ? `— ${r.department}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {user?.role === 'teacher' && (
              <div>
                <Label>Feedback Type</Label>
                <Select value={form.feedback_type} onValueChange={(v) => setForm({ ...form, feedback_type: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select feedback type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cadangan">Cadangan</SelectItem>
                    <SelectItem value="lapor_salah_laku">Lapor salah laku pekerja/pengurusan</SelectItem>
                    <SelectItem value="aduan_ibubapa">Aduan berkenaan ibubapa</SelectItem>
                    <SelectItem value="aduan_pelajar">Aduan berkenaan pelajar</SelectItem>
                    <SelectItem value="lapor_insiden">Lapor insiden atau kemalangan</SelectItem>
                    <SelectItem value="aduan_kemudahan">Aduan mengenai kemudahan/fasiliti</SelectItem>
                    <SelectItem value="lain_lain">Lain-lain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="feedback-subject-input" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} required data-testid="feedback-message-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setComposeOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="send-feedback-button">
                <Send className="w-4 h-4 mr-2" /> Send
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Broadcast Message Dialog - Admin Only */}
      {user?.role === 'admin' && (
        <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
          <DialogContent className="max-w-lg" data-testid="broadcast-compose-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Megaphone className="w-5 h-5 mr-2 inline" /> Send Broadcast Message
              </DialogTitle>
              <DialogDescription>
                Send a message to individuals or groups
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={sendBroadcast} className="space-y-4" data-testid="broadcast-form">
              <div>
                <Label>Send To</Label>
                <Select value={broadcastForm.recipient_type} onValueChange={(v) => setBroadcastForm({ ...broadcastForm, recipient_type: v, recipient_id: '' })}>
                  <SelectTrigger data-testid="broadcast-recipient-type-select">
                    <SelectValue placeholder="Select recipient type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual ({recipients.length} available)</SelectItem>
                    <SelectItem value="all_teachers">All Teachers ({recipientCounts.all_teachers || 0})</SelectItem>
                    <SelectItem value="all_parents">All Parents ({recipientCounts.all_parents || 0})</SelectItem>
                    <SelectItem value="all_students">All Students ({recipientCounts.all_students || 0})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {broadcastForm.recipient_type === 'individual' && (
                <div>
                  <Label>Select Person</Label>
                  <Select value={broadcastForm.recipient_id} onValueChange={(v) => setBroadcastForm({ ...broadcastForm, recipient_id: v })}>
                    <SelectTrigger data-testid="broadcast-individual-select">
                      <SelectValue placeholder="Select person" />
                    </SelectTrigger>
                    <SelectContent>
                      {recipients.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name} {r.department ? `— ${r.department}` : ''} ({r.role})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Subject</Label>
                <Input value={broadcastForm.subject} onChange={(e) => setBroadcastForm({ ...broadcastForm, subject: e.target.value })} required data-testid="broadcast-subject-input" />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea value={broadcastForm.message} onChange={(e) => setBroadcastForm({ ...broadcastForm, message: e.target.value })} rows={6} required data-testid="broadcast-message-input" />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button type="button" variant="outline" onClick={() => setBroadcastOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="send-broadcast-button">
                  <Megaphone className="w-4 h-4 mr-2" /> Send to {broadcastForm.recipient_type === 'individual' ? '1 Person' : broadcastForm.recipient_type.replace('all_', 'All ').replace('_', ' ')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

export default FeedbackSection;
